import axios from 'axios'
import {
  documentContentRequest,
  refusedContentStatus,
} from '@/services/api/documentContent'
import type { FormDocument, PhotoRecord } from '@verifiedit/form-generator'
import { defaultDocumentStatus } from '@/services/api/site'
import { explainDocument } from '@/types/documents'
import Log from '@/utilities/Log'

type ImageRecord = {
  id: number
  name: string | null
  status?: string | null
  uuid: string | null
}

const extensions: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
}

export const storeImage = async function (
  customerId: number | null,
  siteId: number | null,
  userId: number | undefined,
  imageData: string,
  imageTypeId: number,
  fileName?: string | null,
  serviceId?: number | null,
  subServiceId?: number | null,
): Promise<{ id: number; document: FormDocument }> {
  const mimeType = mimeTypeOf(imageData)
  const data = new FormData()

  data.append(
    'imageData',
    new File(
      [decodeDataUri(imageData)],
      fileName ?? defaultFileName(mimeType),
      { type: mimeType },
    ),
  )
  appendValue(data, 'customerId', customerId)
  appendValue(data, 'fileName', fileName)
  appendValue(data, 'imageTypeId', imageTypeId)
  appendValue(data, 'serviceId', serviceId)
  appendValue(data, 'siteId', siteId)
  appendValue(data, 'subServiceId', subServiceId)

  const response = await axios.post('/api/images', data, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  })
  const record: ImageRecord = response?.data?.data
  const document = documentFor(record)

  if (mimeType) {
    document.mimeType = mimeType
  }

  return { document, id: record.id }
}

export const removeImage = async function (imageId: number) {
  const response = await axios.delete(`/api/images/${imageId}`)
  return response?.data?.data?.code
}

/**
 * The photo records behind a saved answer, in the shape `PhotoInput` renders.
 *
 * The batch read states each row's status and never carries bytes, so the content of the ones
 * that have any is fetched per photo afterwards — the same order every other document surface
 * follows: status first, bytes only if the status said there are some.
 *
 * `trackObjectUrl` is the caller's: the view restoring the answer is what revokes the urls
 * fetched for it.
 */
export const getImages = async function (
  imageIds: number[],
  trackObjectUrl: (url: string) => string = (url) => url,
): Promise<PhotoRecord[]> {
  const response = await axios.get('/api/images', {
    params: {
      imageIds: imageIds,
    },
  })
  const records: ImageRecord[] = response.data.data

  return Promise.all(
    records.map((record) => photoRecordFor(record, trackObjectUrl)),
  )
}

/**
 * One photo's bytes, from its own url, as something capture can bind directly — plus the mime
 * type the api sent with them, which for an image record is the only place a mime type exists
 * at all (B54/B83).
 *
 * The caller owns revoking the url (see `objectUrls`).
 */
export const fetchImageContent = async (
  imageId: number,
): Promise<{ content: string; mimeType: string | null }> => {
  const response = await axios.get<Blob>(
    `/api/images/${imageId}/content`,
    documentContentRequest,
  )

  return {
    content: URL.createObjectURL(response.data),
    mimeType: response.data.type || null,
  }
}

async function photoRecordFor(
  record: ImageRecord,
  trackObjectUrl: (url: string) => string,
): Promise<PhotoRecord> {
  const document = documentFor(record)

  if (document.status === 'available') {
    try {
      const { content, mimeType } = await fetchImageContent(record.id)

      document.content = trackObjectUrl(content)
      document.mimeType = mimeType
    } catch (error) {
      // A status that says `available` over bytes nobody can produce is two reads of the same
      // columns disagreeing — reported as the fault it is, and shown as a document with
      // nothing behind it, rather than failing the restore of every other photo on the form
      // (see the register, B90).
      Log.notify(error as Error, 'Photo content contradicted its status')

      document.status = refusedContentStatus(error)
      explainDocument(document)
    }
  }

  return {
    document: document,
    documentUuid: record.uuid ?? null,
    id: record.id,
  }
}

function documentFor(record: ImageRecord): FormDocument {
  // A row with no document uuid has never had a document, whatever status it reports: empty
  // scan columns derive `awaiting_scan`, and passing that through would sit the photo behind a
  // "scan in progress" placeholder forever. The uuid is an identity components key on, so one
  // is made from the record id. Same mapping portal-app makes for the same rows (B83).
  const document: FormDocument = {
    name: record.name ?? null,
    status:
      record.uuid === null ? 'missing' : defaultDocumentStatus(record.status),
    uuid: record.uuid ?? `image-${record.id}`,
  }

  return explainDocument(document)
}

/** Multipart carries strings; a field with no value is left out rather than sent as "null". */
function appendValue(
  data: FormData,
  key: string,
  value: number | string | null | undefined,
): void {
  if (value === null || value === undefined) {
    return
  }

  data.append(key, String(value))
}

const mimeTypeOf = (dataUri: string): string =>
  dataUri.match(/^data:([^;,]+)/)?.[1] ?? ''

/** The data URI's own base64 payload, decoded here rather than fetched back over the network. */
function decodeDataUri(dataUri: string): Uint8Array {
  const binary = atob(dataUri.split(',')[1] ?? '')
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

function defaultFileName(mimeType: string): string {
  const extension =
    extensions[mimeType] ?? mimeType.split('/')[1]?.split('+')[0]

  return extension ? `photo.${extension}` : 'photo'
}
